import { useTranslation } from "react-i18next";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { ColDef, ICellRendererParams, IServerSideDatasource, ValueFormatterParams } from "ag-grid-community";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton} from "@mui/material";
import { Check, DescriptionRounded, Clear } from "@mui/icons-material";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { AxiosError } from "axios";
import { ReactQueryKey } from "../../../../types";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import { getBookingDisambiguationJimpi, bookingsDisambiguationJimpi, deleteBookingDisambiguationJimpi, saveBookingDisambiguationJimpi, getCountDisuabiguationJimpi } from "../../../../api/services";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import useDialog from "../../../../hooks/useDialog";
import DialogCloseButton from "../../../../components/DialogCloseButton";

/**
 * Table component of JIMPI disambiguation
 */
export const JimpiDisambiguationTabPanel = () => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);
    const [idRecord, setIdRecord] = useState<string | undefined>(undefined);
    const acceptDialog = useDialog();
    const rejectDialog = useDialog();

    const disambiguationListMutation = useMutation(getBookingDisambiguationJimpi);
    const disambiguationCountMutation = useMutation(getCountDisuabiguationJimpi);

    const saveBookingDisambiguationMutation = useMutation(saveBookingDisambiguationJimpi);
    const deleteBookingDisambiguationMutation = useMutation(deleteBookingDisambiguationJimpi);

    const countDisambiguationJimpiBadge = useQuery<number>({
        queryKey: [ReactQueryKey.CountDisambiguationJimpiBadge],
        enabled: false,
        onSuccess: (data) => {
            if(countDisambiguationJimpiBadge.data && countDisambiguationJimpiBadge.data !== data) {
                gridRef.current?.api.refreshServerSide();
            }
        }
    });

    const gridRef = useRef<AgGridReact>(null);

    const queryClient = useQueryClient();

    // Column Definition
    const columnDefs = useMemo<ColDef<bookingsDisambiguationJimpi>[]>(() => [
        {
            field: 'rentwayNumber',
            headerName: t('label.jimpiDisambiguationTabRentwayNumberColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'dossierNumber',
            headerName: t('label.jimpiDisambiguationTabDossierNumberColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'dropoffLocation',
            headerName: t('label.jimpiDisambiguationTabDropoffLocationColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'pickupLocation',
            headerName: t('label.jimpiDisambiguationTabPickupLocationColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'customerNr',
            headerName: t('label.jimpiDisambiguationTabCustomerNrColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'dropoffDate',
            headerName: t('label.jimpiDisambiguationTabDropoffDateColumn'),
            suppressColumnsToolPanel: true,
            valueFormatter: (params: ValueFormatterParams<bookingsDisambiguationJimpi, string>) => (
                !!params.value ? dayjs(params.value).format('DD-MM-YYYY HH:mm') : ''
            )
        },
        {
            field: 'pickupDate',
            headerName: t('label.jimpiDisambiguationTabPickupDateColumn'),
            suppressColumnsToolPanel: true,
            valueFormatter: (params: ValueFormatterParams<bookingsDisambiguationJimpi, string>) => (
                !!params.value ? dayjs(params.value).format('DD-MM-YYYY HH:mm') : ''
            )
        },
        {
            field: 'cargroupModel',
            headerName: t('label.jimpiDisambiguationTabcarGroupModelColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'driverSurname',
            headerName: t('label.jimpiDisambiguationTabDriverSurnameColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'driverName',
            headerName: t('label.jimpiDisambiguationTabDriverNameColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: 'idBooking',
            headerName: t('label.jimpiDisambiguationTabIdBookingColumn'),
            suppressColumnsToolPanel: true
        },
        {
            field: '_actions',
            headerName: '',
            lockPosition: 'right',
            pinned: 'right',
            suppressSizeToFit: true,
            resizable: false,
            width: 180,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<bookingsDisambiguationJimpi>) => {
                const handleReject = () => {
                    if(params?.data?.id) {
                        setIdRecord(params?.data?.id.toString()); rejectDialog.handleShow()
                    }
                }
                const handleAccept = () => {
                    if (params?.data?.id) {
                        setIdRecord(params?.data?.id.toString());
                        acceptDialog.handleShow()
                    }
                }
                return (
                    <>
                        <IconButton title={t('label.jimpiDisambiguationTabRejectAction')} color="error" onClick={handleReject} sx={{mr: 2}}>
                            <Clear />
                        </IconButton>

                        <IconButton title={t('label.jimpiDisambiguationTabAcceptAction')} color="success" onClick={handleAccept} sx={{mr: 2}}>
                            <Check />
                        </IconButton>

                        <Link to={`/booking/view-disambiguation/${params?.data?.id.toString()}`} target="_blank">
                            <IconButton title={t('label.details')} color="accent">
                                <DescriptionRounded />
                            </IconButton>
                        </Link>
                    </>
                );
            }
        }
    ], []);

    // DefaultColDef sets props common to all Columns
    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: true,
        unSortIcon: true,
        minWidth: 200
    }), []);

    // load table record
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                let total: number;

                disambiguationCountMutation.mutateAsync().then((response: number) => {
                    total = response;
                    queryClient.setQueryData(
                        [ReactQueryKey.CountDisambiguationJimpiBadge],
                        total
                    );

                    disambiguationListMutation.mutateAsync({
                        start: start,
                        limit: limit
                    }).then((response: bookingsDisambiguationJimpi[]) => {
                        params.success({
                            rowData: response,
                            rowCount: total ?? 0
                        });

                        if (total === 0) {
                            params.api.showNoRowsOverlay();
                        }
                    }).catch((err: AxiosError) => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }).catch((err: AxiosError) => {
                    params.fail();
                    params.api.hideOverlay();
                });
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [countDisambiguationJimpiBadge.data]);
    /** Accept disambiguation **/
    const handleApplyActionClick = useCallback(() => {
        saveBookingDisambiguationMutation.mutateAsync(
            idRecord
        ).then(() => {
            acceptDialog.handleClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.success.default'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            setIdRecord(undefined);
            gridRef.current?.api.refreshServerSide();
        })
            .catch((err: AxiosError) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            });
    }, [idRecord]);
    /** Reject disambiguation **/
    const handleRejectClick = useCallback(() => {
        deleteBookingDisambiguationMutation.mutateAsync(
            idRecord
        ).then(() => {
            rejectDialog.handleClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.success.default'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            setIdRecord(undefined);
            gridRef.current?.api.refreshServerSide();
        })
            .catch((err: AxiosError) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            });
    }, [idRecord]);

    const closeAccept = () => {setIdRecord(undefined); acceptDialog.handleClose()};
    const closeReject = () => {setIdRecord(undefined); rejectDialog.handleClose()};

    return(
        <>
            <AgGridCustomTable
                gridRef={gridRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource= {serverSideDatasource}
                paginationPageSize={25}
                rowModelType="serverSide"
                suppressMultiSort
                suppressDragLeaveHidesColumns
                cacheBlockSize={25}
                enableCellTextSelection
            />
            <CustomLoader
                loading={
                    disambiguationListMutation.isLoading ||
                    disambiguationCountMutation.isLoading ||
                    saveBookingDisambiguationMutation.isLoading ||
                    deleteBookingDisambiguationMutation.isLoading
                }
            />
            <Dialog
                open={acceptDialog.open}
                onClose={acceptDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={closeAccept}/>
                <DialogTitle>
                    {t("label.jimpiDisambiguationViewHeaderTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.confirm_accept_disambiguation")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={closeAccept}>
                        {t("label.cancel")}
                    </Button>
                    <Button onClick={handleApplyActionClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={rejectDialog.open}
                onClose={rejectDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={closeReject}/>
                <DialogTitle>
                    {t("label.jimpiDisambiguationViewHeaderTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.confirm_reject_disambiguation")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={closeReject}>
                        {t("label.cancel")}
                    </Button>
                    <Button onClick={handleRejectClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default JimpiDisambiguationTabPanel;

